import React, { useState, useEffect, useCallback } from 'react';
import { lexiconApi, languagesApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Search, BookOpen, Sparkles, Trash2, Pencil, X, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';

// ── Inline detail panel showing all VF oracle fields ─────────────────────────
function VerbFormationDetail({ entry, onClose, onEnrich, onDelete, onUpdate }) {
  const [editMode, setEditMode] = useState(false);
  const [editFields, setEditFields] = useState({
    translation_en: entry?.translation_en || '',
    formation_type: entry?.formation_type || '',
    notes: entry?.notes || '',
  });

  useEffect(() => {
    setEditFields({
      translation_en: entry?.translation_en || '',
      formation_type: entry?.formation_type || '',
      notes: entry?.notes || '',
    });
    setEditMode(false);
  }, [entry?.entry_id]);

  if (!entry) return null;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b border-border/40 flex-shrink-0">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
              <BookOpen size={11} /> Verb Formation
            </span>
            {entry.ai_enriched && (
              <span className="text-xs text-muted-foreground/60">AI enriched</span>
            )}
          </div>
          <h2 className="text-xl font-bold text-green-400 break-words" data-testid="vfl-detail-word">{entry.word}</h2>
          {entry.ipa && <p className="text-xs text-muted-foreground mt-0.5">/{entry.ipa}/</p>}
        </div>
        <button onClick={onClose} className="ml-2 text-muted-foreground hover:text-foreground shrink-0" data-testid="vfl-detail-close"><X size={18} /></button>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border/30 flex-shrink-0">
        {onEnrich && (
          <button
            className="text-xs bg-green-500/15 hover:bg-green-500/25 text-green-400 flex items-center gap-1 px-2.5 py-1 rounded transition-colors"
            onClick={onEnrich}
            data-testid="vfl-enrich-btn"
          >
            <Sparkles size={11} /> AI Enrich
          </button>
        )}
        {onUpdate && (
          <button
            className="text-xs border border-green-500/30 hover:border-green-400/50 text-green-400 flex items-center gap-1 px-2.5 py-1 rounded transition-colors"
            onClick={() => setEditMode(v => !v)}
            data-testid="vfl-edit-btn"
          >
            <Pencil size={11} /> Edit
          </button>
        )}
        {onDelete && (
          <button
            className="text-xs border border-red-500/30 hover:border-red-400/50 text-red-400 flex items-center gap-1 px-2.5 py-1 rounded transition-colors ml-auto"
            onClick={onDelete}
            data-testid="vfl-delete-btn"
          >
            <Trash2 size={11} /> Delete
          </button>
        )}
      </div>

      {/* Inline edit form */}
      {editMode && (
        <div className="mx-4 mt-3 p-3 rounded-lg border border-green-500/30 bg-green-500/8 space-y-2 flex-shrink-0">
          <div>
            <label className="text-xs text-green-400/70 uppercase tracking-wider">Translation</label>
            <input
              className="w-full mt-1 bg-transparent border border-green-500/20 rounded px-2 py-1 text-sm focus:outline-none focus:border-green-400/60"
              value={editFields.translation_en}
              onChange={e => setEditFields(f => ({ ...f, translation_en: e.target.value }))}
              placeholder="English translation…"
              data-testid="vfl-edit-translation"
            />
          </div>
          <div>
            <label className="text-xs text-green-400/70 uppercase tracking-wider">Formation Type</label>
            <input
              className="w-full mt-1 bg-transparent border border-green-500/20 rounded px-2 py-1 text-sm focus:outline-none focus:border-green-400/60"
              value={editFields.formation_type}
              onChange={e => setEditFields(f => ({ ...f, formation_type: e.target.value }))}
              placeholder="e.g. light-verb, compound-verb…"
              data-testid="vfl-edit-type"
            />
          </div>
          <div>
            <label className="text-xs text-green-400/70 uppercase tracking-wider">Notes</label>
            <textarea
              className="w-full mt-1 bg-transparent border border-green-500/20 rounded px-2 py-1 text-sm focus:outline-none focus:border-green-400/60 resize-none"
              rows={2}
              value={editFields.notes}
              onChange={e => setEditFields(f => ({ ...f, notes: e.target.value }))}
              placeholder="Notes…"
              data-testid="vfl-edit-notes"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded" onClick={() => setEditMode(false)}>Cancel</button>
            <button
              className="text-xs bg-green-500/20 hover:bg-green-500/30 text-green-400 px-3 py-1 rounded transition-colors"
              onClick={async () => { await onUpdate(editFields); setEditMode(false); }}
              data-testid="vfl-edit-save-btn"
            >Save</button>
          </div>
        </div>
      )}

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">

        {/* Translation / Meaning */}
        {(entry.translation_en || entry.translation) && (
          <div className="rounded-lg bg-green-500/10 border border-green-500/20 px-3 py-2">
            <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1">Meaning</p>
            <p className="text-sm">{entry.translation_en || entry.translation}</p>
          </div>
        )}

        {/* Definitions */}
        {Array.isArray(entry.definitions) && entry.definitions.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-green-400 uppercase tracking-wider">Definition</p>
            {entry.definitions.map((def, i) => (
              <div key={i} className="text-sm leading-relaxed">
                {def.pos && <span className="text-xs text-green-400/70 mr-1.5">[{def.pos}]</span>}
                {def.definition}
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-border/30" />

        {/* Components */}
        {Array.isArray(entry.components) && entry.components.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1.5">Components</p>
            <div className="flex flex-wrap gap-1.5">
              {entry.components.map((comp, i) => (
                <span key={i} className="px-2.5 py-1 rounded-full text-sm border border-green-500/30 bg-green-500/10 text-green-300">
                  {comp}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Verb element */}
        {entry.verb_element && (
          <div className="flex items-start gap-2 text-sm">
            <span className="text-xs text-muted-foreground uppercase tracking-wider min-w-[60px] pt-0.5">Verb</span>
            <div>
              <span className="text-green-300 font-medium">{entry.verb_element}</span>
              {entry.verb_element_meaning && <span className="text-muted-foreground ml-2 text-xs">— {entry.verb_element_meaning}</span>}
            </div>
          </div>
        )}

        {/* Non-verb element */}
        {entry.non_verb_element_meaning && (
          <div className="flex items-start gap-2 text-sm">
            <span className="text-xs text-muted-foreground uppercase tracking-wider min-w-[60px] pt-0.5">Noun part</span>
            <span className="text-muted-foreground text-xs">{entry.non_verb_element_meaning}</span>
          </div>
        )}

        {/* Lemma */}
        {entry.lemma && entry.lemma !== entry.word && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-xs text-muted-foreground uppercase tracking-wider min-w-[60px]">Lemma</span>
            <span className="font-medium">{entry.lemma}</span>
          </div>
        )}

        {/* Formation type */}
        {entry.formation_type && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground uppercase tracking-wider min-w-[60px]">Type</span>
            <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/15 border border-green-500/25 text-green-400">{entry.formation_type}</span>
          </div>
        )}

        {/* Morphology */}
        {entry.morphology && (
          <div>
            <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1">Morphology</p>
            <p className="text-xs text-muted-foreground leading-relaxed">{entry.morphology}</p>
          </div>
        )}

        {/* Examples */}
        {Array.isArray(entry.examples) && entry.examples.length > 0 && (
          <>
            <div className="border-t border-border/30" />
            <div>
              <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1.5">Examples</p>
              <div className="space-y-1.5">
                {entry.examples.map((ex, i) => (
                  <p key={i} className="text-sm leading-relaxed border-l-2 border-green-500/30 pl-2.5">{ex}</p>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Related formations */}
        {Array.isArray(entry.related_formations) && entry.related_formations.length > 0 && (
          <>
            <div className="border-t border-border/30" />
            <div>
              <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1.5">Related Formations</p>
              <div className="flex flex-wrap gap-1.5">
                {entry.related_formations.map((rf, i) => (
                  <span key={i} className="px-2 py-0.5 rounded text-xs border border-green-500/20 bg-green-500/8 text-green-300/80">
                    {rf}
                  </span>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Notes */}
        {entry.notes && (
          <>
            <div className="border-t border-border/30" />
            <div>
              <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-1">Notes</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{entry.notes}</p>
            </div>
          </>
        )}

        {!entry.ai_enriched && (
          <p className="text-xs text-muted-foreground/40 text-center pt-2">AI enrichment pending…</p>
        )}
      </div>
    </div>
  );
}

// ── Formation card ─────────────────────────────────────────────────────────────
function FormationCard({ entry, isSelected, onClick }) {
  const def = Array.isArray(entry.definitions) && entry.definitions.length
    ? entry.definitions[0].definition
    : entry.translation_en || entry.translation || '';

  return (
    <button
      className={`w-full text-left p-3 rounded-xl border transition-all hover:border-green-500/40 ${isSelected ? 'border-green-500/60 bg-green-500/10' : 'border-border/40 bg-card/50 hover:bg-card/80'}`}
      onClick={onClick}
      data-testid={`vfl-card-${entry.entry_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-green-400 text-base truncate">{entry.word}</p>
          {def && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{def}</p>}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {entry.ai_enriched
            ? <Sparkles size={12} className="text-green-500/60" />
            : <span className="text-xs text-muted-foreground/40">pending</span>
          }
        </div>
      </div>
      {/* Components */}
      {Array.isArray(entry.components) && entry.components.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {entry.components.map((c, i) => (
            <span key={i} className="px-1.5 py-0.5 rounded-full text-xs bg-green-500/10 border border-green-500/20 text-green-400/80">
              {c}
            </span>
          ))}
          {entry.formation_type && (
            <span className="px-1.5 py-0.5 rounded-full text-xs bg-muted/40 border border-border/40 text-muted-foreground">
              {entry.formation_type}
            </span>
          )}
        </div>
      )}
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function VerbFormationLibrary() {
  const { user } = useAuth();
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState('');
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [enrichedFilter, setEnrichedFilter] = useState('all'); // all | enriched | pending
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Load user languages
  useEffect(() => {
    languagesApi.getUserLanguages()
      .then(({ data }) => {
        // Response is a plain array of user-language objects: [{ language: { code, name }, ... }]
        const raw = Array.isArray(data) ? data : (data.languages || data || []);
        const langs = raw
          .map(l => ({
            code: l.language?.code || l.code || l.language_code || '',
            name: l.language?.name || l.name || l.language_name || l.code || '',
          }))
          .filter(l => l.code);
        setLanguages(langs);
        if (langs.length > 0 && !selectedLang) {
          setSelectedLang(langs[0].code);
        }
      })
      .catch(() => {});
  }, []);

  const fetchFormations = useCallback(async () => {
    if (!selectedLang) return;
    setLoading(true);
    try {
      const { data } = await lexiconApi.getVerbFormations(selectedLang);
      let list = data.entries || [];
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        list = list.filter(e =>
          e.word?.toLowerCase().includes(q) ||
          (e.translation_en || '').toLowerCase().includes(q) ||
          (Array.isArray(e.components) && e.components.join(' ').toLowerCase().includes(q))
        );
      }
      if (enrichedFilter === 'enriched') list = list.filter(e => e.ai_enriched);
      if (enrichedFilter === 'pending') list = list.filter(e => !e.ai_enriched);
      setEntries(list);
      setTotal(data.total || list.length);
    } catch {
      toast.error('Failed to load verb formations');
    } finally {
      setLoading(false);
    }
  }, [selectedLang, search, enrichedFilter]);

  useEffect(() => { fetchFormations(); }, [fetchFormations]);

  const handleEnrich = async (entry) => {
    try {
      const { data } = await lexiconApi.enrichVerbFormation(entry.entry_id);
      if (data.success) {
        toast.success('Enriched!');
        setEntries(prev => prev.map(e => e.entry_id === entry.entry_id ? data.entry : e));
        if (selectedEntry?.entry_id === entry.entry_id) setSelectedEntry(data.entry);
      }
    } catch { toast.error('Enrichment failed'); }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete "${entry.word}"?`)) return;
    try {
      await lexiconApi.deleteVerbFormation(entry.entry_id);
      toast.success('Deleted');
      setEntries(prev => prev.filter(e => e.entry_id !== entry.entry_id));
      if (selectedEntry?.entry_id === entry.entry_id) setSelectedEntry(null);
    } catch { toast.error('Delete failed'); }
  };

  const handleUpdate = async (entry, fields) => {
    try {
      const { data } = await lexiconApi.updateVerbFormation(entry.entry_id, fields);
      if (data.success) {
        toast.success('Updated');
        setEntries(prev => prev.map(e => e.entry_id === entry.entry_id ? data.entry : e));
        if (selectedEntry?.entry_id === entry.entry_id) setSelectedEntry(data.entry);
      }
    } catch { toast.error('Update failed'); }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <BookOpen size={20} className="text-green-400" />
            <h1 className="text-2xl font-bold text-foreground">Verb Formation Library</h1>
          </div>
          <p className="text-sm text-muted-foreground">Compound and light-verb constructions you have saved</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-5">
          {/* Language selector */}
          <select
            className="bg-card border border-border/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-500/50"
            value={selectedLang}
            onChange={e => { setSelectedLang(e.target.value); setSelectedEntry(null); }}
            data-testid="vfl-lang-select"
          >
            {languages.map(l => (
              <option key={l.code} value={l.code}>
                {l.name || l.code}
              </option>
            ))}
          </select>

          {/* Search */}
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              className="w-full bg-card border border-border/50 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-green-500/50"
              placeholder="Search formations…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="vfl-search"
            />
          </div>

          {/* Enrichment filter */}
          <select
            className="bg-card border border-border/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-500/50"
            value={enrichedFilter}
            onChange={e => setEnrichedFilter(e.target.value)}
            data-testid="vfl-enriched-filter"
          >
            <option value="all">All ({total})</option>
            <option value="enriched">AI Enriched</option>
            <option value="pending">Pending enrichment</option>
          </select>

          {/* Refresh */}
          <button
            className="p-2 rounded-lg border border-border/50 hover:border-green-500/40 transition-colors text-muted-foreground hover:text-green-400"
            onClick={fetchFormations}
            title="Refresh"
            data-testid="vfl-refresh-btn"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {/* Main content split */}
        <div className="flex gap-4" style={{ minHeight: '60vh' }}>

          {/* Left: card list */}
          <div className={`flex-1 min-w-0 ${selectedEntry ? 'lg:max-w-sm xl:max-w-md' : ''}`}>
            {loading ? (
              <div className="text-center text-muted-foreground py-12">Loading…</div>
            ) : entries.length === 0 ? (
              <div className="text-center py-16 space-y-2">
                <BookOpen size={36} className="text-green-400/30 mx-auto" />
                <p className="text-muted-foreground">No verb formations saved yet</p>
                <p className="text-xs text-muted-foreground/60">Select 2+ words in the Reader and tap "Save Verb Formation"</p>
              </div>
            ) : (
              <div className="grid gap-2">
                {entries.map(entry => (
                  <FormationCard
                    key={entry.entry_id}
                    entry={entry}
                    isSelected={selectedEntry?.entry_id === entry.entry_id}
                    onClick={() => setSelectedEntry(selectedEntry?.entry_id === entry.entry_id ? null : entry)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right: detail panel */}
          {selectedEntry && (
            <div
              className="shrink-0 w-full lg:w-96 border border-green-500/20 rounded-xl bg-card/60 overflow-hidden"
              style={{ maxHeight: '80vh', position: 'sticky', top: '1rem' }}
              data-testid="vfl-detail-panel"
            >
              <VerbFormationDetail
                entry={selectedEntry}
                onClose={() => setSelectedEntry(null)}
                onEnrich={() => handleEnrich(selectedEntry)}
                onDelete={() => handleDelete(selectedEntry)}
                onUpdate={(fields) => handleUpdate(selectedEntry, fields)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
